import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import { visit } from 'unist-util-visit';
import type { Plugin } from 'unified';

import { TldrPill } from '@/legal/components/TldrPill';
import { SectionSummary } from '@/legal/components/SectionSummary';
import { CalloutWarning } from '@/legal/components/CalloutWarning';
import { LegalReferenceBadge } from '@/legal/components/LegalReferenceBadge';
import { PlainExplanation } from '@/legal/components/PlainExplanation';
import { SubprocessorsPreview } from '@/legal/components/SubprocessorsPreview';
import { slugify } from './tocExtractor';

const directiveToHast: Plugin = () => (tree) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  visit(tree, (node: any) => {
    if (
      node.type === 'containerDirective' ||
      node.type === 'leafDirective' ||
      node.type === 'textDirective'
    ) {
      const data = node.data || (node.data = {});
      data.hName = `directive-${node.name}`;
      data.hProperties = node.attributes ?? {};
    }
  });
};

// Поддержка явных anchor-id в заголовках: `## Кто мы {#section-2-who-we-are}` →
// текст в DOM = "Кто мы", id = "section-2-who-we-are". Без этого плагина
// `tocExtractor` парсит `{#…}` как anchor, но `markdownRenderer` рендерит
// заголовок с slugify(text) — anchor ≠ id, TOC-клик не работает (BUG-LD-002).
const headingIdPlugin: Plugin = () => (tree) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  visit(tree, 'heading', (node: any) => {
    const last = node.children?.[node.children.length - 1];
    if (last?.type === 'text' && typeof last.value === 'string') {
      const m = last.value.match(/^(.*?)\s*\{#([^}]+)\}\s*$/);
      if (m) {
        last.value = m[1];
        node.data = node.data ?? {};
        node.data.hProperties = node.data.hProperties ?? {};
        node.data.hProperties.id = m[2];
      }
    }
  });
};

interface Props {
  content: string;
}

function extractText(children: unknown): string {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(extractText).join('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (children && typeof children === 'object' && 'props' in (children as any)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return extractText((children as any).props.children);
  }
  return '';
}

export function MarkdownRenderer({ content }: Props) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkDirective, headingIdPlugin, directiveToHast]}
      components={{
        h2: ({ children, id, ...props }) => {
          const finalId = id ?? slugify(extractText(children));
          return (
            <h2
              {...props}
              id={finalId}
              className="mt-16 scroll-mt-24 font-display text-xl font-semibold dark:font-bold text-text-primary md:text-3xl tracking-[-0.02em]"
            >
              {children}
            </h2>
          );
        },
        h3: ({ children, id, ...props }) => {
          const finalId = id ?? slugify(extractText(children));
          return (
            <h3
              {...props}
              id={finalId}
              className="mt-12 scroll-mt-24 text-base font-semibold text-text-primary md:text-xl"
            >
              {children}
            </h3>
          );
        },
        p: ({ children }) => (
          <p className="my-4 leading-relaxed text-text-primary">{children}</p>
        ),
        ul: ({ children }) => (
          <ul className="my-4 list-disc space-y-2 pl-6 text-text-primary">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="my-4 list-decimal space-y-2 pl-6 text-text-primary">{children}</ol>
        ),
        a: ({ children, href }) => (
          <a href={href} className="text-text-link underline-offset-2 hover:underline">
            {children}
          </a>
        ),
        table: ({ children }) => (
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">{children}</table>
          </div>
        ),
        th: ({ children }) => (
          <th className="border border-border bg-surface-subtle px-3 py-2 text-left font-semibold text-text-primary">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border border-border px-3 py-2 text-text-primary">{children}</td>
        ),
        // Custom directives — react-markdown component map is typed for HTML
        // elements; custom hName values need cast workaround.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ['directive-tldr' as never]: ({ children, minutes }: any) => (
          <TldrPill minutes={minutes}>{children}</TldrPill>
        ),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ['directive-summary' as never]: ({ children }: any) => (
          <SectionSummary>{children}</SectionSummary>
        ),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ['directive-callout-warning' as never]: ({ children }: any) => (
          <CalloutWarning>{children}</CalloutWarning>
        ),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ['directive-badge' as never]: ({ children }: any) => (
          <LegalReferenceBadge>{children}</LegalReferenceBadge>
        ),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ['directive-plain' as never]: ({ children }: any) => (
          <PlainExplanation>{children}</PlainExplanation>
        ),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ['directive-subprocessors-preview' as never]: () => <SubprocessorsPreview />,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}
